import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Repeat } from "lucide-react";
import api, { ASSET_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/orders").then((r) => setOrders(r.data)),
      api.get("/membership/me").then((r) => setMemberships(r.data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-2xl font-extrabold">
          Welcome back, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-gray-500">Your promotional kit orders.</p>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          {/* Orders */}
          <section>
            <h2 className="mb-3 text-lg font-bold">Orders</h2>
            {orders.length === 0 ? (
              <div className="text-center">
                <p className="text-gray-500">No orders yet.</p>
                <Link to="/" className="btn-primary mt-4">
                  Order Custom Kits
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <Link
                    key={o.id}
                    to={`/order-confirmation/${o.order_number}`}
                    className="card flex items-center gap-4 p-4 transition hover:shadow-md"
                  >
                    {o.logo_filename ? (
                      <img
                        src={`${ASSET_URL}/uploads/${o.logo_filename}`}
                        alt="Logo"
                        className="h-14 w-14 rounded border border-gray-200 object-contain p-1"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded border border-dashed border-gray-300 text-[10px] text-gray-400">
                        No logo
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-bold">{o.order_number}</p>
                      <p className="text-sm text-gray-500">
                        {o.event_name || "—"} · {fmt(o.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-extrabold">${o.total?.toFixed(2)}</p>
                      <span className="text-xs font-semibold uppercase text-fairway-600">
                        {o.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* Memberships */}
          <section>
            <h2 className="mb-3 text-lg font-bold">Refill Membership</h2>
            {memberships.length === 0 ? (
              <div className="card flex items-center justify-between p-4">
                <p className="text-sm text-gray-600">
                  You don't have a refill membership yet.
                </p>
                <Link to="/membership" className="btn-outline">
                  Join — $14/year
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {memberships.map((m) => (
                  <div key={m.id} className="card flex items-center gap-4 p-4">
                    <Repeat className="text-fairway-600" size={22} />
                    <div className="flex-1">
                      <p className="font-bold capitalize">{m.status} membership</p>
                      <p className="text-sm text-gray-500">
                        Next refill: {fmt(m.next_refill_date)} ·{" "}
                        {(m.refills || []).length} shipped
                      </p>
                    </div>
                    <span className="font-extrabold">
                      ${m.price?.toFixed(2)}/yr
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
